import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getAdminDashboard } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [error, setError] = useState(null);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getAdminDashboard();
            
            if (response.success) {
                setDashboardData(response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch dashboard data');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message || 'Failed to fetch dashboard data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // Refresh dashboard data every 5 minutes
        const interval = setInterval(fetchDashboardData, 300000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="container-xxl">
                <div className="d-flex justify-content-center align-items-center min-vh-50">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-xxl">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="container-xxl">
                <div className="alert alert-info" role="alert">
                    No dashboard data available
                </div>
            </div>
        );
    }

    const { users, trades, transactions } = dashboardData;

    // Calculate percentages for progress bars
    const activeUsersPercent = users?.total > 0 ? (users.active / users.total) * 100 : 0;
    const activeTradersPercent = trades?.total > 0 ? (trades.active / trades.total) * 100 : 0;
    const profitTradesPercent = trades?.total > 0 ? (trades.profitLoss?.profitCount / trades.total) * 100 : 0;
    const pendingTransactionsPercent = transactions?.total > 0 ? (transactions.pending / transactions.total) * 100 : 0;

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Admin Dashboard" sidebutton={true} />
            
            <div className="row">
                {/* Users Stats */}
                <div className="col-xl-3 col-sm-6">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="icon-box icon-box-lg bg-primary-light rounded-circle">
                                    <i className="fa fa-users text-primary"></i>
                                </div>
                                <div className="ms-3">
                                    <h2 className="mb-0 font-w600">{users?.total || 0}</h2>
                                    <p className="mb-0">Total Users</p>
                                </div>
                            </div>
                            <div className="d-flex flex-column mt-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="fs-12">Active Users ({users?.active || 0})</span>
                                    <span className="fs-12">{activeUsersPercent.toFixed(1)}%</span>
                                </div>
                                <ProgressBar now={activeUsersPercent} variant="primary" className="mb-2" style={{height: "5px"}} />
                                <div className="d-flex justify-content-between">
                                    <p className="mb-0 fs-13">Today's New: {users?.today || 0}</p>
                                    <p className="mb-0 fs-13">Last 30 Days: {users?.last30Days || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trades Stats */}
                <div className="col-xl-3 col-sm-6">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="icon-box icon-box-lg bg-success-light rounded-circle">
                                    <i className="fa fa-chart-line text-success"></i>
                                </div>
                                <div className="ms-3">
                                    <h2 className="mb-0 font-w600">{trades?.total || 0}</h2>
                                    <p className="mb-0">Total Trades</p>
                                </div>
                            </div>
                            <div className="d-flex flex-column mt-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="fs-12">Active Trades ({trades?.active || 0})</span>
                                    <span className="fs-12">{activeTradersPercent.toFixed(1)}%</span>
                                </div>
                                <ProgressBar now={activeTradersPercent} variant="success" className="mb-2" style={{height: "5px"}} />
                                <div className="d-flex justify-content-between">
                                    <p className="mb-0 fs-13">Today's Trades: {trades?.today || 0}</p>
                                    <p className="mb-0 fs-13">Success Rate: {profitTradesPercent.toFixed(1)}%</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Profit/Loss Stats */}
                <div className="col-xl-3 col-sm-6">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="icon-box icon-box-lg bg-warning-light rounded-circle">
                                    <i className="fa fa-dollar-sign text-warning"></i>
                                </div>
                                <div className="ms-3">
                                    <h2 className="mb-0 font-w600">${trades?.profitLoss?.totalProfit?.toFixed(2) || '0.00'}</h2>
                                    <p className="mb-0">Total Profit</p>
                                </div>
                            </div>
                            <div className="d-flex flex-column mt-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="fs-12">Profit/Loss Ratio</span>
                                    <span className="fs-12">{trades?.profitLoss?.profitCount || 0}/{trades?.profitLoss?.lossCount || 0}</span>
                                </div>
                                <ProgressBar>
                                    <ProgressBar variant="success" now={profitTradesPercent} key={1} style={{height: "5px"}} />
                                    <ProgressBar variant="danger" now={100 - profitTradesPercent} key={2} style={{height: "5px"}} />
                                </ProgressBar>
                                <div className="d-flex justify-content-between mt-2">
                                    <p className="mb-0 fs-13 text-success">Profit: ${trades?.profitLoss?.totalProfit?.toFixed(2) || '0.00'}</p>
                                    <p className="mb-0 fs-13 text-danger">Loss: ${trades?.profitLoss?.totalLoss?.toFixed(2) || '0.00'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Transactions Stats */}
                <div className="col-xl-3 col-sm-6">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="icon-box icon-box-lg bg-info-light rounded-circle">
                                    <i className="fa fa-exchange-alt text-info"></i>
                                </div>
                                <div className="ms-3">
                                    <h2 className="mb-0 font-w600">{transactions?.total || 0}</h2>
                                    <p className="mb-0">Total Transactions</p>
                                </div>
                            </div>
                            <div className="d-flex flex-column mt-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="fs-12">Pending ({transactions?.pending || 0})</span>
                                    <span className="fs-12">{pendingTransactionsPercent.toFixed(1)}%</span>
                                </div>
                                <ProgressBar now={pendingTransactionsPercent} variant="info" className="mb-2" style={{height: "5px"}} />
                                <div className="d-flex justify-content-between">
                                    <p className="mb-0 fs-13">Today: {transactions?.today || 0}</p>
                                    <p className="mb-0 fs-13">Pending: {transactions?.pending || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trade Types Distribution */}
                {trades?.byType && trades.byType.length > 0 && (
                    <div className="col-xl-6">
                        <Card>
                            <Card.Header>
                                <Card.Title>Trade Types Distribution</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="table-responsive">
                                    <table className="table table-borderless mb-0">
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Total Trades</th>
                                                <th>Active Trades</th>
                                                <th>Distribution</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trades.byType.map(type => {
                                                const typePercent = trades.total > 0 ? (type.count / trades.total) * 100 : 0;
                                                const activePercent = type.count > 0 ? (type.activeCount / type.count) * 100 : 0;
                                                return (
                                                    <tr key={type._id}>
                                                        <td>
                                                            <Badge bg="primary">
                                                                {type._id?.toUpperCase() || 'N/A'}
                                                            </Badge>
                                                        </td>
                                                        <td>{type.count || 0}</td>
                                                        <td>{type.activeCount || 0}</td>
                                                        <td style={{width: "30%"}}>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span className="fs-12">{typePercent.toFixed(1)}%</span>
                                                                <span className="fs-12 text-success">{activePercent.toFixed(1)}% Active</span>
                                                            </div>
                                                            <ProgressBar>
                                                                <ProgressBar variant="primary" now={typePercent} key={1} style={{height: "5px"}} />
                                                                <ProgressBar variant="success" now={activePercent} key={2} style={{height: "5px"}} />
                                                            </ProgressBar>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                )}

                {/* Transaction Types Distribution */}
                {transactions?.byType && transactions.byType.length > 0 && (
                    <div className="col-xl-6">
                        <Card>
                            <Card.Header>
                                <Card.Title>Transaction Types Distribution</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="table-responsive">
                                    <table className="table table-borderless mb-0">
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Count</th>
                                                <th>Total Amount</th>
                                                <th>Distribution</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.byType.map(type => {
                                                const countPercent = transactions.total > 0 ? (type.count / transactions.total) * 100 : 0;
                                                const totalAmount = transactions.byType.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
                                                const amountPercent = totalAmount > 0 ? (type.totalAmount / totalAmount) * 100 : 0;
                                                return (
                                                    <tr key={type._id}>
                                                        <td>
                                                            <Badge bg={type._id === 'deposit' ? 'success' : 'warning'}>
                                                                {type._id?.toUpperCase() || 'N/A'}
                                                            </Badge>
                                                        </td>
                                                        <td>{type.count || 0}</td>
                                                        <td>${type.totalAmount?.toFixed(2) || '0.00'}</td>
                                                        <td style={{width: "30%"}}>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span className="fs-12">{countPercent.toFixed(1)}% Count</span>
                                                                <span className="fs-12">{amountPercent.toFixed(1)}% Amount</span>
                                                            </div>
                                                            <ProgressBar>
                                                                <ProgressBar 
                                                                    variant={type._id === 'deposit' ? 'success' : 'warning'} 
                                                                    now={amountPercent} 
                                                                    style={{height: "5px"}} 
                                                                />
                                                            </ProgressBar>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;

